'use client';

import { forwardRef, useCallback, useContext, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableState } from '../../hooks/use-controllable-state';
import { ChipGroupContext } from '../chip-group/context/chip-group.context';
import { InputChipContext } from './context/input-chip.context';
import { inputChipRootRecipe } from './styles/input-chip-root.styles';
import type { InputChipRootPropsInterface } from './types/input-chip-root.types';

/**
 * @since 10.0.0
 */
export const InputChipRoot = forwardRef<HTMLSpanElement, InputChipRootPropsInterface>(
  (props: InputChipRootPropsInterface, forwardedRef) => {
    const chipGroupContext = useContext(ChipGroupContext);

    const {
      children,
      elevated = chipGroupContext.elevated || false,
      className,
      css: cssProp,
      disabled = chipGroupContext.disabled || false,
      isOpen,
      onClose,
      ...rest
    } = props;

    const styles = css(inputChipRootRecipe.raw({ elevated, disabled }), cssProp);

    const [openState, setOpenState] = useControllableState({
      prop: isOpen,
      defaultProp: true,
    });

    const [labelText, setLabelText] = useState('');

    const handleClose = useCallback(() => {
      setOpenState(false);
      onClose?.();
    }, [onClose, setOpenState]);

    if (!openState) {
      return null;
    }

    return (
      <InputChipContext.Provider value={{ disabled, handleClose, labelText, setLabelText }}>
        <span
          className={cx('c11n', styles, className)}
          data-c11n-component="InputChip.Root"
          data-c11n-version={__C11N_VERSION__}
          ref={forwardedRef}
          {...rest}
        >
          {children}
        </span>
      </InputChipContext.Provider>
    );
  },
);

InputChipRoot.displayName = 'InputChip.Root';
